'use client'

import { useState, useCallback } from 'react'
import { useInView } from '@/hooks/useInView'
import {
  LayoutDashboard,
  MessageSquare,
  Calendar,
  FileText,
  Zap,
  BarChart3,
  Trophy,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/* ── Feature Data ──────────────────────────────────────────── */
interface Feature {
  id: string
  icon: LucideIcon
  title: string
  description: string
}

const FEATURES: Feature[] = [
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    title: 'Smart Dashboard',
    description:
      'See everything in one place. Leads, jobs, crews, invoices, and revenue all update in real time.',
  },
  {
    id: 'followups',
    icon: MessageSquare,
    title: 'Instant Follow-Ups',
    description:
      'Never lose another lead. AI follows up through text, email, and call so every prospect is answered fast.',
  },
  {
    id: 'scheduling',
    icon: Calendar,
    title: 'Simple Scheduling',
    description:
      'Drag-and-drop calendar syncs with your crews in real time. Weather-aware scheduling protects your timeline.',
  },
  {
    id: 'estimates',
    icon: FileText,
    title: 'Fast Estimates',
    description:
      'Pull satellite measurements, auto-generate estimates, and send branded proposals in under 5 minutes.',
  },
  {
    id: 'ai',
    icon: Zap,
    title: 'AI Automation',
    description:
      'Sierra AI qualifies leads, books appointments, and follows up 24/7 — while you focus on closing.',
  },
  {
    id: 'revenue',
    icon: BarChart3,
    title: 'Revenue Tracking',
    description:
      'Know exactly where every dollar comes from. Track ROI on every lead source and marketing channel.',
  },
]

/* ── Icon Background Colors ───────────────────────────────── */
const ICON_COLORS: Record<string, { bg: string; text: string }> = {
  dashboard: { bg: 'bg-red-600/10', text: 'text-red-500' },
  followups: { bg: 'bg-blue-600/10', text: 'text-blue-400' },
  scheduling: { bg: 'bg-amber-600/10', text: 'text-amber-400' },
  estimates: { bg: 'bg-emerald-600/10', text: 'text-emerald-400' },
  ai: { bg: 'bg-violet-600/10', text: 'text-violet-400' },
  revenue: { bg: 'bg-cyan-600/10', text: 'text-cyan-400' },
}

/* ── Delay class lookup for staggered children ───────────── */
const DELAY_CLASSES = [
  'anim-delay-1',
  'anim-delay-2',
  'anim-delay-3',
  'anim-delay-4',
  'anim-delay-5',
  'anim-delay-6',
]

/* ── Main Component ───────────────────────────────────────── */
export function FeatureExplorer() {
  const { ref, isInView } = useInView()
  const [explored, setExplored] = useState<Set<string>>(new Set())
  const [showToast, setShowToast] = useState(false)

  const handleExplore = useCallback((id: string) => {
    setExplored((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      // Trigger achievement when all explored
      if (next.size === FEATURES.length) {
        setShowToast(true)
        setTimeout(() => setShowToast(false), 4000)
      }
      return next
    })
  }, [])

  const progress = (explored.size / FEATURES.length) * 100
  const viewCls = isInView ? 'in-view' : ''

  return (
    <section className="section noise-overlay relative">
      <div className="section-inner" ref={ref}>
        {/* ── Section Header ──────────────────────────────── */}
        <div className={`anim-fade-in-up ${viewCls} text-center mb-8 sm:mb-10`}>
          <p className="text-xs uppercase tracking-[0.2em] text-red-500 font-semibold mb-3">
            Features
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight max-w-2xl mx-auto mb-4">
            Everything You Need,{' '}
            <span className="gradient-text-red">One Platform.</span>
          </h2>

          {/* ── Progress Bar ──────────────────────────────── */}
          <div className="max-w-sm mx-auto mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500">
                Explore all features
              </span>
              <span className="text-xs text-zinc-400 font-medium tabular-nums">
                {explored.size}/{FEATURES.length} explored
              </span>
            </div>
            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-red-600 to-red-500 rounded-full"
                style={{
                  width: `${progress}%`,
                  transition: 'width 0.4s ease-out',
                }}
              />
            </div>
          </div>
        </div>

        {/* ── Bento Grid ──────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
          {FEATURES.map((feature, i) => {
            const Icon = feature.icon
            const isExplored = explored.has(feature.id)
            const colors = ICON_COLORS[feature.id]

            return (
              <div
                key={feature.id}
                onMouseEnter={() => handleExplore(feature.id)}
                onClick={() => handleExplore(feature.id)}
                className={`anim-fade-in-up ${DELAY_CLASSES[i] ?? ''} ${viewCls} glass-card-premium p-5 sm:p-6 md:p-7 group cursor-pointer relative hover:scale-[1.02] hover:-translate-y-1 transition-transform duration-300 ${
                  isExplored ? 'border-red-600/20' : ''
                }`}
                style={{ perspective: '800px' }}
              >
                {/* Explored indicator dot */}
                {isExplored && (
                  <div className="absolute top-3 right-3 animate-[scale-pop_0.3s_ease-out]">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                  </div>
                )}

                {/* Hover glow overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-red-600/[0.04] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-[inherit]" />

                <div className="relative z-10">
                  {/* Icon */}
                  <div
                    className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl ${colors?.bg ?? 'bg-red-600/10'} flex items-center justify-center mb-4 sm:mb-5 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Icon
                      size={22}
                      className={`${colors?.text ?? 'text-red-500'} transition-colors`}
                    />
                  </div>

                  {/* Title */}
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2">
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Achievement Toast ─────────────────────────────── */}
      <div
        className={`fixed bottom-20 sm:bottom-8 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
          showToast
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-10 scale-95 pointer-events-none'
        }`}
      >
        <div className="flex items-center gap-3 px-5 sm:px-6 py-3 sm:py-3.5 rounded-2xl bg-zinc-900 border border-red-600/40 shadow-2xl glow-red-sm">
          <Trophy size={20} className="text-amber-400 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-white">
              Achievement Unlocked: Feature Master! 🏆
            </p>
            <p className="text-xs text-zinc-400">
              You explored all 6 features
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
